interface ActivityPoint {
  hours: number;
  percentage: number;
}

interface ActivityProfile {
  room: ActivityPoint[];
  fridge: ActivityPoint[];
}

// Map feeding ratios to their corresponding activity profile
export const RATIO_TO_PROFILE_MAP: Record<string, string> = {
  '1-1-1': '1-1-1',
  '1-1-0.7': '1-1-1',
  '1-1-0.5': '1-1-1',
  '1-3-3': '1-3-3',
  '1-2-1.4': '1-3-3',
  '1-2-1': '1-3-3',
  '1-5-5': '1-5-5',
  '1-5-3.5': '1-5-5',
  '1-6-3': '1-5-5'
};

export const ACTIVITY_PROFILES: Record<string, ActivityProfile> = {
  // Base profile for 1:1:1, 1:1:0.7, and 1:1:0.5 (similar behavior due to same flour ratio) 
  '1-1-1': { // Also used for 1-1-0.7 and 1-1-0.5
    room: [
      { hours: 1, percentage: 20 },
      { hours: 2, percentage: 60 },
      { hours: 3, percentage: 100 },
      { hours: 6, percentage: 80 },
      { hours: 8, percentage: 50 },
      { hours: 12, percentage: 30 },
      { hours: 24, percentage: 10 }
    ],
    fridge: [
      { hours: 6, percentage: 20 },
      { hours: 12, percentage: 60 },
      { hours: 24, percentage: 100 },
      { hours: 36, percentage: 80 },
      { hours: 48, percentage: 50 },
      { hours: 72, percentage: 30 },
      { hours: 96, percentage: 10 }
    ]
  },
  // Profile for 1:3:3, 1:2:1.4, and 1:2:1 (similar behavior due to doubled/tripled flour)
  '1-3-3': { // Also used for 1-2-1.4 and 1-2-1
    room: [
      { hours: 2, percentage: 20 },
      { hours: 4, percentage: 60 },
      { hours: 7, percentage: 100 },
      { hours: 12, percentage: 80 },
      { hours: 18, percentage: 50 },
      { hours: 24, percentage: 30 },
      { hours: 36, percentage: 10 }
    ],
    fridge: [
      { hours: 18, percentage: 20 },
      { hours: 36, percentage: 60 },
      { hours: 72, percentage: 100 },
      { hours: 96, percentage: 80 },
      { hours: 120, percentage: 50 },
      { hours: 144, percentage: 30 },
      { hours: 168, percentage: 10 }
    ]
  },
  // Profile for 1:5:5, 1:5:3.5, and 1:6:3 (similar behavior due to high flour ratio)
  '1-5-5': { // Also used for 1-5-3.5 and 1-6-3
    room: [
      { hours: 4, percentage: 20 },
      { hours: 8, percentage: 60 },
      { hours: 12, percentage: 100 },
      { hours: 20, percentage: 80 },
      { hours: 30, percentage: 50 },
      { hours: 40, percentage: 30 },
      { hours: 60, percentage: 10 }
    ],
    fridge: [
      { hours: 30, percentage: 20 },
      { hours: 60, percentage: 60 },
      { hours: 120, percentage: 100 },
      { hours: 144, percentage: 80 },
      { hours: 168, percentage: 50 },
      { hours: 192, percentage: 30 },
      { hours: 240, percentage: 10 }
    ]
  }
};

export const getCurrentActivity = (
  hoursSinceLastFeed: number,
  storage: 'room' | 'fridge',
  roomTemperature?: number,
  feedingRatio: string = '1-1-1'
): number => {
  // Get the activity profile based on feeding ratio
  const profileKey = RATIO_TO_PROFILE_MAP[feedingRatio] || '1-1-1';
  const profile = ACTIVITY_PROFILES[profileKey];
  const activityPoints = storage === 'room' ? profile.room : profile.fridge;

  let adjustedHours = hoursSinceLastFeed;
  if (storage === 'room' && roomTemperature) {
    if (roomTemperature >= 16 && roomTemperature < 20) {
      // Cold room: Add 2 hours to effective time
      adjustedHours = Math.max(0, adjustedHours - 2);
    } else if (roomTemperature > 25 && roomTemperature <= 30) {
      // Warm room: Subtract 1 hour from effective time
      adjustedHours = Math.max(0, adjustedHours + 1);
    }
  }

  const maxHours = activityPoints[activityPoints.length - 1].hours;
  if (adjustedHours > maxHours) {
    return activityPoints[activityPoints.length - 1].percentage;
  }

  for (let i = 0; i < activityPoints.length - 1; i++) {
    const current = activityPoints[i];
    const next = activityPoints[i + 1];

    if (adjustedHours <= current.hours) {
      return current.percentage;
    }

    if (adjustedHours > current.hours && adjustedHours <= next.hours) {
      const progress = (adjustedHours - current.hours) / (next.hours - current.hours);
      return current.percentage + (next.percentage - current.percentage) * progress;
    }
  }

  return activityPoints[activityPoints.length - 1].percentage;
};